import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, X, Crosshair, Dumbbell, Utensils, Swords, Repeat, Timer, Loader2, Rocket, AlertTriangle, Skull } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../context/AppContext';
import { toast } from 'sonner';

const SECTION_CONFIG = [
  { title: 'CONFIRMATION DE CAP', icon: Crosshair, color: 'text-emerald-400', border: 'border-emerald-500/30', bg: 'bg-emerald-500/5' },
  { title: 'IMPACT ENTRAINEMENT', icon: Dumbbell, color: 'text-orange-400', border: 'border-orange-500/30', bg: 'bg-orange-500/5' },
  { title: 'IMPACT LOGISTIQUE', icon: Utensils, color: 'text-blue-400', border: 'border-blue-500/30', bg: 'bg-blue-500/5' },
  { title: 'ORDRE D\'ACTION', icon: Swords, color: 'text-red-400', border: 'border-red-500/30', bg: 'bg-red-500/5' },
];

const ULTIMATUM_HOURS = 48;

function parseBriefing(text) {
  if (!text) return [];
  const sections = [];
  const patterns = [
    /\*\*CONFIRMATION DE CAP\*\*/i,
    /\*\*IMPACT ENTRA[IÎ]NEMENT\*\*/i,
    /\*\*IMPACT LOGISTIQUE\*\*/i,
    /\*\*ORDRE D[''\u2019]ACTION\*\*/i,
  ];

  let remaining = text;
  for (let i = 0; i < patterns.length; i++) {
    const match = remaining.match(patterns[i]);
    if (match) {
      remaining = remaining.slice(match.index + match[0].length);
      const nextMatch = i < patterns.length - 1 ? remaining.match(patterns[i + 1]) : null;
      const content = nextMatch ? remaining.slice(0, nextMatch.index).trim() : remaining.trim();
      if (nextMatch) remaining = remaining.slice(nextMatch.index);
      sections.push({ ...SECTION_CONFIG[i], content: content.replace(/^\n+/, '').replace(/\n+$/, '') });
    }
  }

  if (sections.length === 0 && text.trim()) {
    sections.push({ ...SECTION_CONFIG[0], content: text.trim() });
  }

  return sections;
}

function formatCountdown(ms) {
  if (ms <= 0) return { h: '00', m: '00', s: '00', expired: true };
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return {
    h: String(h).padStart(2, '0'),
    m: String(m).padStart(2, '0'),
    s: String(s).padStart(2, '0'),
    expired: false,
    totalSec,
  };
}

function CountdownTimer({ createdAt, language, onExpired }) {
  const [remaining, setRemaining] = useState(null);

  const calcRemaining = useCallback(() => {
    if (!createdAt) return 0;
    const created = new Date(createdAt).getTime();
    const expiry = created + ULTIMATUM_HOURS * 60 * 60 * 1000;
    return expiry - Date.now();
  }, [createdAt]);

  useEffect(() => {
    setRemaining(calcRemaining());
    const interval = setInterval(() => {
      const ms = calcRemaining();
      setRemaining(ms);
      if (ms <= 0) {
        clearInterval(interval);
        onExpired?.();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [calcRemaining, onExpired]);

  if (remaining === null) return null;

  const cd = formatCountdown(remaining);
  const urgencyLevel = remaining > 24 * 60 * 60 * 1000 ? 'normal' : remaining > 6 * 60 * 60 * 1000 ? 'warning' : 'critical';

  const colorMap = {
    normal: 'text-amber-400 border-amber-500/40 bg-amber-500/10',
    warning: 'text-orange-400 border-orange-500/40 bg-orange-500/10',
    critical: 'text-red-400 border-red-500/40 bg-red-500/10',
  };

  if (cd.expired) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex items-center justify-center gap-3 p-3 rounded-xl border-2 ${colorMap[urgencyLevel]}`}
      data-testid="ultimatum-countdown"
    >
      <AlertTriangle className={`w-4 h-4 ${urgencyLevel === 'critical' ? 'animate-pulse' : ''}`} />
      <div className="text-center">
        <p className="text-[9px] font-heading uppercase tracking-[0.3em] opacity-80">
          {language === 'fr' ? 'Ultimatum d\'action' : 'Action Ultimatum'}
        </p>
        <div className="flex items-center gap-1 mt-1 font-mono">
          <span className="text-xl font-black tabular-nums">{cd.h}</span>
          <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ repeat: Infinity, duration: 1 }} className="text-lg font-bold">:</motion.span>
          <span className="text-xl font-black tabular-nums">{cd.m}</span>
          <motion.span animate={{ opacity: [1, 0.3, 1] }} transition={{ repeat: Infinity, duration: 1 }} className="text-lg font-bold">:</motion.span>
          <span className="text-xl font-black tabular-nums">{cd.s}</span>
        </div>
      </div>
      <Timer className={`w-4 h-4 ${urgencyLevel === 'critical' ? 'animate-pulse' : ''}`} />
    </motion.div>
  );
}

function ExpiredMessage({ language, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="p-6 text-center space-y-4"
      data-testid="ultimatum-expired"
    >
      <motion.div
        animate={{ rotate: [0, -5, 5, -5, 0] }}
        transition={{ repeat: Infinity, duration: 3 }}
      >
        <Skull className="w-12 h-12 text-red-500 mx-auto" />
      </motion.div>
      <h3 className="font-heading text-lg uppercase tracking-wider text-red-400">
        {language === 'fr' ? 'Taxe d\'inaction' : 'Inaction Tax'}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
        {language === 'fr'
          ? 'Le plan provisoire s\'est autodétruit. 48 heures d\'inaction. L\'hésitation a un prix. Retourne dans tes réglages et redemande un nouveau plan au Coach.'
          : 'The provisional plan self-destructed. 48 hours of inaction. Hesitation has a price. Go back to settings and request a new plan from the Coach.'}
      </p>
      <Button
        onClick={onClose}
        className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-heading uppercase tracking-wider"
        data-testid="expired-close-btn"
      >
        <Swords className="w-4 h-4 mr-2" />
        {language === 'fr' ? 'Compris. Je reagis.' : 'Understood. I act.'}
      </Button>
    </motion.div>
  );
}

const TacticalBriefingModal = ({ briefing, provisionalPlan, provisionalPlanId, newMission, createdAt, onClose, onAccepted, language }) => {
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [expired, setExpired] = useState(false);

  // Check initial expiration state
  useEffect(() => {
    if (createdAt) {
      const created = new Date(createdAt).getTime();
      const expiry = created + ULTIMATUM_HOURS * 60 * 60 * 1000;
      if (Date.now() > expiry) setExpired(true);
    }
  }, [createdAt]);

  if (!briefing) return null;

  const sections = parseBriefing(briefing);
  const exercises = Array.isArray(provisionalPlan) ? provisionalPlan : [];

  const handleAcceptMission = async () => {
    if (!provisionalPlanId || exercises.length === 0) return;
    setAccepting(true);
    try {
      await api.post('/users/accept-provisional-plan', {
        provisional_plan_id: provisionalPlanId,
        exercises,
        new_mission: newMission || '',
      });
      setAccepted(true);
      toast.success(language === 'fr' ? 'Mission acceptee! Plan loge dans ton carnet de tir.' : 'Mission accepted! Plan logged to your fire log.');
      onAccepted?.();
    } catch {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[999] flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
        onClick={onClose}
        data-testid="tactical-briefing-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border border-red-500/30 bg-card"
          style={{ boxShadow: '0 0 60px rgba(239,68,68,0.15)' }}
          onClick={e => e.stopPropagation()}
          data-testid="tactical-briefing-modal"
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-gradient-to-b from-card via-card to-card/95 p-4 pb-3 border-b border-border/50">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10 transition-colors"
              data-testid="briefing-close-btn"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
            <div className="flex items-center gap-3">
              <motion.div
                animate={{ rotate: [0, -5, 5, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
              >
                <Shield className="w-7 h-7 text-red-500" />
              </motion.div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-red-400">YUMIX</p>
                <h2 className="font-heading text-lg uppercase tracking-tight text-foreground" data-testid="briefing-title">
                  {language === 'fr' ? 'Briefing de Redirection Tactique' : 'Tactical Redirect Briefing'}
                </h2>
              </div>
            </div>
          </div>

          {/* EXPIRED STATE */}
          {expired ? (
            <ExpiredMessage language={language} onClose={onClose} />
          ) : (
            <>
              {/* Countdown Timer */}
              {createdAt && exercises.length > 0 && !accepted && (
                <div className="px-4 pt-4">
                  <CountdownTimer
                    createdAt={createdAt}
                    language={language}
                    onExpired={() => setExpired(true)}
                  />
                </div>
              )}

              {/* Briefing Sections */}
              <div className="p-4 space-y-3">
                {sections.map((section, i) => {
                  const Icon = section.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -15 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 * (i + 1) }}
                      className={`p-3 rounded-xl border ${section.border} ${section.bg}`}
                      data-testid={`briefing-section-${i}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon className={`w-4 h-4 ${section.color}`} />
                        <p className={`text-[10px] font-heading uppercase tracking-[0.2em] ${section.color} font-bold`}>
                          {section.title}
                        </p>
                      </div>
                      <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line">
                        {section.content}
                      </p>
                    </motion.div>
                  );
                })}

                {/* PROVISIONAL PLAN */}
                {exercises.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="mt-2 p-4 rounded-xl border-2 border-red-500/40 bg-gradient-to-br from-red-500/5 to-orange-500/5"
                    data-testid="provisional-plan"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Dumbbell className="w-5 h-5 text-red-400" />
                      <p className="text-xs font-heading uppercase tracking-wider text-red-400 font-bold">
                        {language === 'fr' ? 'Séance d\'Introduction Immediate' : 'Immediate Intro Session'}
                      </p>
                    </div>

                    <div className="space-y-2">
                      {exercises.map((ex, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.9 + i * 0.1 }}
                          className="flex items-center gap-3 p-2.5 bg-card/60 rounded-lg border border-border/50"
                          data-testid={`prov-exercise-${i}`}
                        >
                          <span className="w-6 h-6 bg-red-500/20 text-red-400 rounded-full flex items-center justify-center text-[10px] font-black shrink-0">
                            {i + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-foreground truncate">{ex.name}</p>
                            <div className="flex items-center gap-3 mt-0.5">
                              {ex.sets && ex.reps && (
                                <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                  <Repeat className="w-3 h-3" /> {ex.sets}x{ex.reps}
                                </span>
                              )}
                              {ex.tempo && (
                                <span className="text-[10px] text-red-400 font-bold">
                                  Tempo {ex.tempo}
                                </span>
                              )}
                              {ex.rest_seconds && (
                                <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                  <Timer className="w-3 h-3" /> {ex.rest_seconds}s
                                </span>
                              )}
                            </div>
                            {ex.notes && (
                              <p className="text-[10px] text-muted-foreground italic mt-0.5">{ex.notes}</p>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Action buttons */}
              <div className="p-4 pt-2 space-y-2">
                {exercises.length > 0 && !accepted && (
                  <Button
                    onClick={handleAcceptMission}
                    disabled={accepting}
                    className="w-full h-12 font-heading uppercase tracking-wider text-base bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white shadow-lg shadow-red-500/20"
                    data-testid="accept-mission-btn"
                  >
                    {accepting ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Rocket className="w-5 h-5 mr-2" />
                    )}
                    {language === 'fr' ? 'Accepter la mission initiale' : 'Accept initial mission'}
                  </Button>
                )}

                {accepted && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full h-12 rounded-md flex items-center justify-center gap-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 font-heading uppercase tracking-wider text-sm"
                    data-testid="mission-accepted-confirmation"
                  >
                    <Swords className="w-4 h-4" />
                    {language === 'fr' ? 'Mission logee dans ton carnet de tir' : 'Mission logged to your fire log'}
                  </motion.div>
                )}

                <Button
                  onClick={onClose}
                  variant={exercises.length > 0 && !accepted ? 'outline' : 'default'}
                  className={`w-full font-heading uppercase tracking-wider ${
                    exercises.length > 0 && !accepted
                      ? 'text-muted-foreground'
                      : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white'
                  }`}
                  data-testid="briefing-confirm-btn"
                >
                  <Swords className="w-4 h-4 mr-2" />
                  {accepted
                    ? (language === 'fr' ? 'Fermer et commencer' : 'Close and begin')
                    : (language === 'fr' ? 'Compris. En avant.' : 'Understood. Forward.')
                  }
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TacticalBriefingModal;
